import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Users, Calendar, Mic2, MessageSquare, TrendingUp,
  Eye, Clock, ArrowUpRight, Plus, Images, Heart
} from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { formatRelativeTime, isFutureDate } from '../../utils/helpers'
import { staggerContainer, staggerItem } from '../../hooks/useAnimations'
import { Button, Card, Badge } from '../../components/common'

const StatCard = ({ title, value, icon: Icon, change, color = 'primary' }) => (
  <motion.div
    className="bg-white rounded-xl p-6 shadow-lg"
    variants={staggerItem}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-500 text-sm">{title}</p>
        <p className="text-3xl font-heading font-bold text-dark-100 mt-1">{value}</p>
        {change && (
          <p className={`text-sm mt-2 flex items-center gap-1 ${change > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            <TrendingUp className={`w-4 h-4 ${change < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(change)}% from last month
          </p>
        )}
      </div>
      <div className={`w-12 h-12 bg-${color}-100 rounded-xl flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
  </motion.div>
)

const QuickAction = ({ title, description, icon: Icon, href, color = 'primary' }) => (
  <Link to={href}>
    <motion.div
      className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow group"
      whileHover={{ y: -2 }}
    >
      <div className={`w-12 h-12 bg-${color}-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
      <h3 className="font-semibold text-dark-100 mb-1">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </motion.div>
  </Link>
)

const Dashboard = () => {
  const { data: events } = useCollection('events', { limitCount: 5 })
  const { data: sermons } = useCollection('sermons', { limitCount: 5 })
  const { data: formSubmissions } = useCollection('formSubmissions', { limitCount: 20 })

  const upcomingEvents = events?.filter(e => isFutureDate(e.date))?.slice(0, 3) || []
  const recentSermons = sermons?.slice(0, 3) || []
  const totalSubmissions = formSubmissions?.length || 0
  const unreadSubmissions = formSubmissions?.filter(s => s.status === 'new' || !s.read)?.length || 0

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Dashboard</h1>
          <p className="text-gray-500">Welcome back! Here's what's happening.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/admin/events">
            <Button variant="outline" icon={Plus}>New Event</Button>
          </Link>
          <Link to="/admin/sermons">
            <Button variant="primary" icon={Plus}>New Sermon</Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <motion.div
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        <StatCard
          title="Upcoming Events"
          value={upcomingEvents.length}
          icon={Calendar}
          color="primary"
        />
        <StatCard
          title="Total Sermons"
          value={sermons?.length || 0}
          icon={Mic2}
          color="secondary"
        />
        <StatCard
          title="Form Submissions"
          value={totalSubmissions}
          icon={MessageSquare}
          color="accent"
        />
        <StatCard
          title="Unread Messages"
          value={unreadSubmissions}
          icon={Eye}
          color="emerald"
        />
      </motion.div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-dark-100 mb-4">Quick Actions</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction
            title="Edit Hero"
            description="Update homepage slides"
            icon={Images}
            href="/admin/hero"
            color="primary"
          />
          <QuickAction
            title="Add Event"
            description="Create a new event"
            icon={Calendar}
            href="/admin/events"
            color="secondary"
          />
          <QuickAction
            title="Upload Sermon"
            description="Add a new sermon"
            icon={Mic2}
            href="/admin/sermons"
            color="accent"
          />
          <QuickAction
            title="Manage Team"
            description="Update leadership"
            icon={Users}
            href="/admin/leadership"
            color="emerald"
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming Events */}
        <Card animate={false} className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-dark-100">Upcoming Events</h2>
            <Link to="/admin/events" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
              View All <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {upcomingEvents.length > 0 ? (
            <div className="space-y-4">
              {upcomingEvents.map((event) => {
                // Parse date as local to avoid timezone issues
                const dateParts = event.date?.split('-') || []
                const eventDate = dateParts.length === 3
                  ? new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
                  : new Date(event.date)
                return (
                <div key={event.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex flex-col items-center justify-center">
                    <span className="text-xs text-primary-600 uppercase">
                      {eventDate.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                    <span className="text-lg font-bold text-primary-900">
                      {eventDate.getDate()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-dark-100 truncate">{event.title}</h3>
                    <p className="text-sm text-gray-500">{event.time} • {event.location}</p>
                  </div>
                  <Badge variant="primary" size="sm">{event.category || 'general'}</Badge>
                </div>
              )})}

            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No upcoming events</p>
              <Link to="/admin/events">
                <Button variant="ghost" size="sm" className="mt-2">
                  Create Event
                </Button>
              </Link>
            </div>
          )}
        </Card>

        {/* Recent Sermons */}
        <Card animate={false} className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-dark-100">Recent Sermons</h2>
            <Link to="/admin/sermons" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
              View All <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {recentSermons.length > 0 ? (
            <div className="space-y-4">
              {recentSermons.map((sermon) => (
                <div key={sermon.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-16 h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                    {sermon.thumbnailUrl ? (
                      <img src={sermon.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Mic2 className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-dark-100 truncate">{sermon.title}</h3>
                    <p className="text-sm text-gray-500">{sermon.speaker}</p>
                  </div>
                  <span className="text-xs text-gray-400">{formatRelativeTime(sermon.date)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Mic2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No sermons yet</p>
              <Link to="/admin/sermons">
                <Button variant="ghost" size="sm" className="mt-2">
                  Add Sermon
                </Button>
              </Link>
            </div>
          )}
        </Card>
      </div>

      {/* Recent Submissions */}
      <Card animate={false} className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-dark-100">Recent Form Submissions</h2>
          <Link to="/admin/submissions" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
            View All <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {totalSubmissions > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b">
                  <th className="pb-3 font-medium">Type</th>
                  <th className="pb-3 font-medium">Name</th>
                  <th className="pb-3 font-medium">Email</th>
                  <th className="pb-3 font-medium">Date</th>
                  <th className="pb-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(formSubmissions || [])
                  .sort((a, b) => new Date(b.submittedAt || b.createdAt) - new Date(a.submittedAt || a.createdAt))
                  .slice(0, 5)
                  .map((submission, idx) => (
                    <tr key={submission.id || idx} className="border-b last:border-0">
                      <td className="py-3">
                        <Badge
                          variant={
                            submission.formType === 'contact' ? 'primary' :
                            submission.formType === 'prayer' ? 'secondary' :
                            submission.formType === 'joinGroup' ? 'primary' :
                            submission.formType === 'baptism' ? 'secondary' :
                            'accent'
                          }
                          size="sm"
                        >
                          {submission.formType || 'Unknown'}
                        </Badge>
                      </td>
                      <td className="py-3 font-medium text-dark-100">{submission.name}</td>
                      <td className="py-3 text-gray-600">{submission.email}</td>
                      <td className="py-3 text-gray-500 text-sm">
                        {formatRelativeTime(submission.submittedAt || submission.createdAt)}
                      </td>
                      <td className="py-3">
                        {submission.status === 'read' || submission.read ? (
                          <Badge variant="gray" size="sm">Read</Badge>
                        ) : (
                          <Badge variant="accent" size="sm" dot>New</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No form submissions yet</p>
          </div>
        )}
      </Card>
    </div>
  )
}

export default Dashboard
